import {IssueOpenedIcon, CommentIcon, ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react'
import {Button, ToggleSwitch} from '@primer/react'
import {useCallback, useState} from 'react'

import type {TriggerConfig, TriggerType} from '../config-parser'
import {ALL_TRIGGER_TYPES, TRIGGER_METADATA} from '../config-parser'

import styles from './ConfigSections.module.css'
import {SectionHeader} from './SectionHeader'

type TriggersSectionProps = {
  triggers: TriggerConfig[]
  onChange: (triggers: TriggerConfig[]) => void
}

const TRIGGER_ICONS: Record<TriggerType, React.ReactNode> = {
  'issue-create': <IssueOpenedIcon size={16} className={styles.cardRowIcon} />,
  'issue-comment': <CommentIcon size={16} className={styles.cardRowIcon} />,
} as const

export function TriggersSection({triggers, onChange}: TriggersSectionProps) {
  const [expandedTriggers, setExpandedTriggers] = useState<Set<TriggerType>>(
    () => new Set(triggers.filter(t => t.enabled && t.filter).map(t => t.type)),
  )

  const toggleTrigger = useCallback(
    (type: TriggerType) => {
      onChange(
        triggers.map(t => {
          if (t.type !== type) return t
          const nowEnabled = !t.enabled
          // Clear filter when disabling
          return nowEnabled ? {...t, enabled: true} : {...t, enabled: false, filter: undefined}
        }),
      )
      if (triggers.find(t => t.type === type)?.enabled) {
        setExpandedTriggers(prev => {
          const next = new Set(prev)
          next.delete(type)
          return next
        })
      }
    },
    [triggers, onChange],
  )

  const updateFilter = useCallback(
    (type: TriggerType, filter: string) => {
      onChange(triggers.map(t => (t.type === type ? {...t, filter: filter || undefined} : t)))
    },
    [triggers, onChange],
  )

  const toggleAdvanced = useCallback(
    (type: TriggerType) => {
      setExpandedTriggers(prev => {
        const next = new Set(prev)
        if (next.has(type)) {
          next.delete(type)
          onChange(triggers.map(t => (t.type === type ? {...t, filter: undefined} : t)))
        } else {
          next.add(type)
        }
        return next
      })
    },
    [triggers, onChange],
  )

  return (
    <div className={styles.section}>
      <SectionHeader
        title="Triggers"
        subtitle={
          <>
            Events that cause this agent to run.
            {!triggers.some(t => t.enabled) && (
              <span className={styles.validationInline}> Select at least one trigger.</span>
            )}
          </>
        }
      />

      <div className={styles.cardList}>
        {ALL_TRIGGER_TYPES.map(type => {
          const meta = TRIGGER_METADATA[type]
          const trigger = triggers.find(t => t.type === type)
          const isEnabled = trigger?.enabled ?? false
          const isExpanded = expandedTriggers.has(type)

          return (
            <div key={type} className={styles.cardRow} data-testid={`trigger-${type}`}>
              {TRIGGER_ICONS[type]}
              <div className={styles.cardContent}>
                <p className={styles.cardTitle} id={`trigger-label-${type}`}>
                  {meta.label}
                </p>
                <p className={styles.cardDescription}>{meta.description}</p>
              </div>
              <div className={styles.cardActions}>
                {isEnabled && meta.supportsFilter && (
                  <Button
                    variant="invisible"
                    size="small"
                    onClick={() => toggleAdvanced(type)}
                    aria-expanded={isExpanded}
                    data-testid={`trigger-advanced-${type}`}
                  >
                    Advanced
                    {isExpanded ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
                  </Button>
                )}
                <ToggleSwitch
                  checked={isEnabled}
                  onClick={() => toggleTrigger(type)}
                  aria-labelledby={`trigger-label-${type}`}
                  size="small"
                />
              </div>
              {isEnabled && meta.supportsFilter && (
                <div
                  className={`${styles.advancedContent} ${isExpanded ? styles.advancedContentOpen : styles.advancedContentClosed}`}
                >
                  <div className={styles.advancedContentInner}>
                    <p className={styles.filterHelp}>
                      Filter which issues trigger this action. Use <code>-</code> to exclude.{' '}
                      <a
                        href="https://docs.github.com/en/issues/tracking-your-work-with-issues/using-issues/filtering-and-searching-issues-and-pull-requests"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Search syntax docs
                      </a>
                    </p>
                    <input
                      type="text"
                      className={styles.filterInput}
                      value={trigger?.filter ?? ''}
                      onChange={e => updateFilter(type, e.target.value)}
                      placeholder="e.g. -label:do_not_triage -has:assignee"
                      aria-label={`Filter query for ${TRIGGER_METADATA[type].label}`}
                      data-testid={`trigger-filter-${type}`}
                    />
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
